import React from 'react';
import { Button, Popconfirm, Tag, Tooltip } from 'antd';
import { DeleteOutlined, LinkOutlined } from '@ant-design/icons';
import BasicRackDevice from './BasicRackDevice';
import EnhancedRackDevice from './EnhancedRackDevice';
import EmptyUSlot from './EmptyUSlot';
import {
  generateColorFromId,
  getPortsForRackDevice,
  resolveDiagramTemplate,
  getTemplateLabel,
  getTemplateMinHeight,
  getDiagramConfig,
  enhancedTheme,
} from './utils';

const U_BOX_HEIGHT = 76;

const RackDiagramGroup = ({
  group,
  mode,
  data,
  availableDevices,
  diagramDevices,
  diagramPorts,
  selectValues,
  renderEquipmentName,
  onSelectDevice,
  onDeleteMapping,
  selectedPortId,
  connectedPortIds,
  onPortClick,
  onRegisterAnchor,
}) => {
  if (group.type === 'empty') {
    return group.uNumbers.map((uNumber) => (
      <EmptyUSlot
        key={uNumber}
        uNumber={uNumber}
        selectValue={selectValues[uNumber]}
        availableDevices={availableDevices}
        onSelectDevice={onSelectDevice}
      />
    ));
  }

  const { deviceId, uNumbers: groupUNumbers } = group;
  const mapping = (data.mapping || []).find(
    (m) => m.u_number === groupUNumbers[0],
  );
  const device = mapping?.rackdevice;
  const totalHeight =
    groupUNumbers.length * U_BOX_HEIGHT - 8 * (groupUNumbers.length - 1);

  if (mode === 'basic') {
    return (
      <BasicRackDevice
        key={`group-${groupUNumbers.join('-')}`}
        deviceId={deviceId}
        device={device}
        groupUNumbers={groupUNumbers}
        totalHeight={totalHeight}
        renderEquipmentName={renderEquipmentName}
        onDeleteMapping={onDeleteMapping}
        startU={groupUNumbers[0]}
      />
    );
  }

  const deviceRecord =
    diagramDevices[String(deviceId)] ||
    availableDevices.find((d) => String(d._id) === String(deviceId));
  const ports = getPortsForRackDevice(deviceId, diagramPorts, deviceRecord);
  const template = resolveDiagramTemplate(deviceRecord);
  const diagramConfig = getDiagramConfig(deviceRecord);
  const templateLabel = getTemplateLabel(template);
  const deviceHeight = Math.max(
    totalHeight,
    getTemplateMinHeight(template, diagramConfig),
  );
  const hasDeviceregistry = Boolean(deviceRecord?.deviceregistry);
  const linkedCount = ports.filter((p) => p.connected_portregistry).length;

  return (
    <div
      key={`group-${groupUNumbers.join('-')}`}
      style={{ marginBottom: '8px' }}
    >
      <div style={{ display: 'flex', gap: '16px', alignItems: 'stretch' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {groupUNumbers.map((uNumber) => (
            <div
              key={uNumber}
              style={{
                border: `2px solid ${enhancedTheme.rackBorder}`,
                padding: '8px',
                backgroundColor: '#fff',
                fontWeight: 'bold',
                borderRadius: '4px',
                minWidth: '80px',
                minHeight: '60px',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#434343',
              }}
            >
              {uNumber}U
            </div>
          ))}
        </div>
        <div style={{ flex: 1, position: 'relative' }}>
          <div
            style={{
              border: `2px solid ${enhancedTheme.deviceBorder}`,
              padding: '10px 14px',
              backgroundColor: enhancedTheme.deviceBg,
              minHeight: `${deviceHeight}px`,
              borderRadius: '6px',
              boxShadow: '0 1px 4px rgba(24, 144, 255, 0.15)',
              position: 'relative',
              boxSizing: 'border-box',
              overflow: 'visible',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 10,
                padding: '6px 10px',
                background: enhancedTheme.deviceHeaderBg,
                borderRadius: 4,
                border: '1px solid #91d5ff',
              }}
            >
              <div>
                <a
                  href={`/rackdevice/view/${deviceId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#096dd9',
                    fontSize: 13,
                    fontWeight: 'bold',
                    marginRight: 8,
                  }}
                >
                  {deviceRecord?.equipment_name || 'Device'}
                </a>
                <Tag color="blue">{templateLabel}</Tag>
                {hasDeviceregistry ? (
                  <Tag color="green" icon={<LinkOutlined />}>
                    {ports.length} port{ports.length !== 1 ? 's' : ''}
                    {linkedCount > 0 ? ` · ${linkedCount} linked` : ''}
                  </Tag>
                ) : (
                  <Tooltip title="Edit rack device and link Device Registry to enable ports">
                    <Tag color="orange">No Device Registry link</Tag>
                  </Tooltip>
                )}
              </div>
              <Popconfirm
                title="Are you sure you want to remove this device mapping?"
                onConfirm={() => onDeleteMapping(groupUNumbers[0])}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                />
              </Popconfirm>
            </div>
            <EnhancedRackDevice
              deviceRecord={deviceRecord}
              ports={ports}
              selectedPortId={selectedPortId}
              connectedPortIds={connectedPortIds}
              onPortClick={onPortClick}
              onRegisterAnchor={onRegisterAnchor}
            />
          </div>
        </div>
      </div>
      <div
        style={{
          borderBottom: `1px solid ${enhancedTheme.rackBorder}`,
          paddingBottom: '8px',
          marginTop: 4,
        }}
      />
    </div>
  );
};

export default RackDiagramGroup;
